/**
 * Helpers de descarga de archivos (PDF, Excel) en el navegador — Fase 7.3
 *
 * Reemplaza a `jsPDF.save()` interno: usamos `URL.createObjectURL` +
 * `<a download>` + `click()` + cleanup. Misma lógica que jsPDF hace
 * internamente, pero queda bajo nuestro control:
 *   - testeable (jsdom no tiene URL.createObjectURL nativo, lo mockeamos)
 *   - reutilizable entre PDFs (jspdf) y Excels (exceljs)
 *   - sin depender del polyfill interno de cada lib
 *
 * Documentos relacionados:
 *   - DECISIONES.md #034 (stack reportes cliente)
 */

/**
 * Dispara la descarga de un Blob en el navegador.
 * Genérico: sirve para PDF, Excel, CSV, lo que sea que devuelva un Blob.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Sanitiza un string para usarlo como nombre de archivo.
 * Reemplaza caracteres inválidos en Windows/macOS/Linux con guión bajo.
 */
export function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Devuelve un sufijo de timestamp legible para nombres de archivo.
 * Formato: YYYYMMDD-HHmm (ej: 20260525-1432).
 */
export function timestampSuffix(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `-${pad(date.getHours())}${pad(date.getMinutes())}`
  );
}

/**
 * Arma un nombre de archivo de reporte estándar:
 *   `reporte-{nombre}-{timestamp}.{ext}`
 */
export function buildReporteFilename(
  nombre: string,
  ext: 'pdf' | 'xlsx',
  date: Date = new Date(),
): string {
  const limpio = sanitizeFilename(nombre);
  return `reporte-${limpio}-${timestampSuffix(date)}.${ext}`;
}
